import * as React from "react"
import type {
  PaymentDetailsType,
  RecentPendingPaymentsType,
  WeeklyPaymentsChartType,
} from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import moment from "moment"
import type { DateRange } from "react-day-picker"
import { useReactToPrint } from "react-to-print"

import { canViewThisPage } from "@/lib/api/auth"
import {
  getPaymentHistory,
  getRecentPendingPayments,
  getWeeklyPayments,
} from "@/lib/api/billing"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AreaChartComponent } from "@/components/charts/areachart"
import { BarChartComponent } from "@/components/charts/barchart"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import { billingAdjustmentColumns } from "@/components/table/columns/billing-adjustment-columns"
import { paymentDetailsColumns } from "@/components/table/columns/payment-details-columns"
import { ExportOperationModal } from "@/components/table/operations/export-operations"

export const Route = createFileRoute("/_home/reports")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_reports")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: ReportsPage,
})

function ReportsPage() {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [showAllPayments, setShowAllPayments] = React.useState(false)
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })

  const [weeklyPaymentsDate, setWeeklyPaymentsDate] = React.useState<DateRange>(
    {
      from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      to: new Date(),
    },
  )

  const [search, setSearch] = React.useState<string>("")
  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)

  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)

  const {
    data: paymentDetails,
    isError,
    isLoading,
  } = useQuery<PaymentDetailsType[]>({
    queryKey: ["paymentDetails", date],
    queryFn: async () => {
      const from = date.from ?? new Date()
      const to = date.to ?? new Date()
      return await getPaymentHistory(
        moment(from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        moment(to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      )
    },
  })

  const { data: weeklyPayments } = useQuery<WeeklyPaymentsChartType[]>({
    queryKey: ["weeklyPayments"],
    queryFn: getWeeklyPayments,
  })

  const { data: pendingPayments } = useQuery<RecentPendingPaymentsType[]>({
    queryKey: ["pendingPayments"],
    queryFn: getRecentPendingPayments,
  })

  const contentRef = React.useRef<HTMLDivElement>(null)
  const reactToPrintFn = useReactToPrint({ contentRef })

  const renderPaymentsTable = () => {
    try {
      if (!pendingPayments || pendingPayments.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={4} className="text-center">
              No recent pending payments available
            </TableCell>
          </TableRow>
        )
      }

      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Consumer</TableHead>
              <TableHead>Acc No.</TableHead>
              <TableHead>Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingPayments
              .slice(0, showAllPayments ? 10 : 5)
              .map((payment: RecentPendingPaymentsType, index) => (
                <TableRow key={index}>
                  <TableCell>{payment.consumer_name}</TableCell>
                  <TableCell>{payment.account_number}</TableCell>
                  <TableCell>₱{payment.amount.toFixed(2)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )
    } catch (error) {
      console.error("Error rendering payments table:", error)
      return null
    }
  }

  return (
    <main className="space-y-4 p-4">
      <Header>Reports</Header>
      <div className="space-y-4">
        <Tabs defaultValue="Payments" className="w-full">
          <div className="overflow-x-auto px-2 sm:px-0">
            <TabsList className="flex w-full max-w-full gap-2 border bg-white dark:bg-transparent sm:w-max">
              <TabsTrigger
                className="min-w-[120px] flex-1 whitespace-nowrap px-4 py-2 text-center data-[state=active]:bg-primary data-[state=active]:text-white"
                value="Payments"
              >
                Payments
              </TabsTrigger>
              <TabsTrigger
                className="min-w-[150px] flex-1 whitespace-nowrap px-4 py-2 text-center data-[state=active]:bg-primary data-[state=active]:text-white"
                value="Weekly Payments"
              >
                Weekly Payments
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="Payments">
            <div className="grid grid-cols-1 md:items-stretch">
              <DataTable
                columns={paymentDetailsColumns}
                data={paymentDetails ?? []}
                search={{
                  field: "consumer_name",
                  label: "consumer",
                  value: search,
                  onChange: setSearch,
                }}
                defaultPage={10}
                isLoading={isLoading}
                isError={isError}
              >
                <div className="flex w-auto justify-end md:w-full">
                  <ExportOperationModal
                    tableData={paymentDetails ?? [{}]}
                    fileName="Reports"
                    showExportModal={showExportModal}
                    setShowExportModal={setShowExportModal}
                  />
                  <DateRangePicker date={date} setDate={setDate} />
                </div>
              </DataTable>
            </div>
          </TabsContent>
          <TabsContent value="Weekly Payments">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Card className="bg-background md:col-span-2">
                <CardHeader className="flex items-center justify-between gap-2 md:flex-row">
                  <CardTitle>Weekly Payments</CardTitle>
                </CardHeader>
                <CardContent className="pl-2 pr-10">
                  <AreaChartComponent
                    className="h-[300px]"
                    data={weeklyPayments}
                  />
                </CardContent>
              </Card>
              <Card className="bg-background">
                <CardHeader className="flex flex-row items-center justify-between py-3">
                  <CardTitle>Recent Pending</CardTitle>
                  <Button
                    onClick={() => setShowAllPayments(!showAllPayments)}
                    variant="outline"
                    size="sm"
                  >
                    {showAllPayments ? "Show Less" : "See All"}
                  </Button>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {renderPaymentsTable()}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  )
}
